import {
  useMutation,
  useQueryClient,
  type Query,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  agentToolsApi,
  type MCPConnectionDTO,
  type PaginatedMCPConnectionsResponse,
  type UpdateMCPConnectionPayload,
} from "../../../services/agent-tools-service";
import { MCP_CONNECTIONS_QUERY_KEY } from "./useMCPConnections";

type ApiError = {
  response?: {
    data?: { error?: { message?: string }; detail?: string; message?: string };
  };
  message?: string;
};

const extractErrorMessage = (error: ApiError, fallback: string): string => {
  const data = error.response?.data;
  return (
    data?.error?.message ||
    data?.detail ||
    data?.message ||
    error.message ||
    fallback
  );
};

const isListPageQuery = (query: Query): boolean => {
  const key = query.queryKey;
  return (
    Array.isArray(key) &&
    key[0] === MCP_CONNECTIONS_QUERY_KEY[0] &&
    typeof key[1] === "number"
  );
};

type Options = {
  onSuccess?: (dto: MCPConnectionDTO) => void;
};

type Variables = {
  id: string;
  payload: UpdateMCPConnectionPayload;
};

type MutationContext = {
  previousDetail?: MCPConnectionDTO;
};

const detailKey = (id: string) =>
  [...MCP_CONNECTIONS_QUERY_KEY, "detail", id] as const;

const writeConnectionToCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  next: MCPConnectionDTO,
) => {
  queryClient.setQueryData<MCPConnectionDTO>(detailKey(id), next);
  queryClient.setQueriesData<PaginatedMCPConnectionsResponse>(
    { predicate: isListPageQuery },
    (old) => {
      if (!old?.items) return old;
      return {
        ...old,
        items: old.items.map((item) => (item.id === id ? next : item)),
      };
    },
  );
};

export const useUpdateMCPConnection = (options: Options = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: Variables) =>
      agentToolsApi.updateMCPConnection(id, payload),
    // Optimistically patch the cached connection so role/active toggles feel
    // instant. We snapshot the previous detail for rollback on error.
    onMutate: async ({ id, payload }): Promise<MutationContext> => {
      await queryClient.cancelQueries({ queryKey: detailKey(id) });
      const previousDetail = queryClient.getQueryData<MCPConnectionDTO>(
        detailKey(id),
      );
      if (previousDetail) {
        writeConnectionToCaches(queryClient, id, {
          ...previousDetail,
          ...payload,
        } as MCPConnectionDTO);
      }
      return { previousDetail };
    },
    onSuccess: (updated, { id }) => {
      // Reconcile with the authoritative server response.
      writeConnectionToCaches(queryClient, id, updated);
      options.onSuccess?.(updated);
    },
    onError: (error: ApiError, { id }, context) => {
      // Roll back the optimistic patch.
      if (context?.previousDetail) {
        writeConnectionToCaches(queryClient, id, context.previousDetail);
      }
      toast.error(extractErrorMessage(error, t("mcpPage.mutation.updateFailed")));
    },
  });
};
